import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload } from 'lucide-react';
import { uploadTranscription } from '@/lib/upload-transcription';
import { useToast } from '@/components/ui/use-toast';

interface ContentTranscriptFieldProps {
  value: string;
  onChange: (url: string) => void;
  id?: string;
  label?: string;
}

/**
 * Transcript field for the meditation and oefening config forms: paste a URL,
 * or upload the file and let it land in the content-audio bucket.
 *
 * The transcripts are the ElevenLabs .json exports with word timings, which is
 * what gives the text its word-by-word highlight; .srt and .vtt work too. They
 * were linked from Dropbox, which the player has to fetch() while the audio is
 * already playing, so a dropped connection there means the text never appears.
 * Uploading here avoids repeating that.
 *
 * The URL box stays: an external link is occasionally what you want.
 */
export function ContentTranscriptField({
  value,
  onChange,
  id = 'transcriptURL',
  label = 'Transcriptie (.json met woordtijden, of .srt)',
}: ContentTranscriptFieldProps) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    setUploading(true);
    try {
      const { url, bytes, lines } = await uploadTranscription(file);
      onChange(url);
      toast({
        title: 'Transcriptie geüpload',
        description: `${lines} regels, ${Math.round(bytes / 1024)} kB.`,
      });
    } catch (err) {
      console.error('Transcript upload failed:', err);
      toast({
        variant: 'destructive',
        title: 'Uploaden mislukt',
        description: err instanceof Error ? err.message : 'Probeer het opnieuw.',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-2">
        <Input
          id={id}
          name={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Plak een URL of upload een bestand"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="shrink-0 gap-2"
        >
          <Upload className="h-4 w-4" />
          {uploading ? 'Bezig...' : 'Upload'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,.srt,.vtt,application/json,text/plain"
          className="hidden"
          onChange={handleFile}
        />
      </div>
    </div>
  );
}
